import type React from "react";
import { forwardRef } from "react";
import { Box, Typography } from "@mui/material";
import type {
  IStreamData,
  IThumbnailTemplate,
} from "../../../../../types/thumbnail-template";
import liveStreamingLogo from "../../../../../assets/livestreaminglogo.png";
import {
  formatDateToLongString,
  formatTime,
} from "../../../../../utils/FormatDate";

interface ConferenceTemplateProps {
  streamData: IStreamData;
  template: IThumbnailTemplate;
  style?: React.CSSProperties;
  customBackground?: string;
}

const ConferenceTemplate = forwardRef<HTMLDivElement, ConferenceTemplateProps>(
  ({ streamData, template, style, customBackground }, ref) => {
    const eventName = streamData?.title || "Name of Event";
    const formattedDate = formatDateToLongString(streamData?.date);
    const formattedTime = formatTime(streamData?.shoot_from || "");
    const venueName =
      streamData?.venue_name ||
      streamData?.location ||
      streamData?.service?.venue?.name ||
      "Venue Name";
    const city = streamData?.address || "Harare, Zimbabwe";

    console.log("conference stream data", streamData);

    const backgroundImage =
      customBackground ||
      streamData?.custom_background ||
      template.background_image;

    return (
      <Box
        ref={ref}
        sx={{
          position: "relative",
          backgroundImage: `url(${backgroundImage})`,
          backgroundColor: "#6B7280",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          color: "#ffffff",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          fontFamily: '"Montserrat" !important',
          "& *": {
            fontFamily: '"Montserrat" !important',
          },
          padding: "60px",
          ...style,
        }}
      >
        {/* Event Name */}
        <Typography
          sx={{
            fontSize: "4em",
            fontWeight: 700,
            textAlign: "center",
            marginBottom: "20px",
            textTransform: "uppercase",
            letterSpacing: "0.02em",
          }}
        >
          {eventName}
        </Typography>

        {/* Date */}
        <Typography
          sx={{
            fontSize: "3em",
            fontWeight: 600,
            textAlign: "center",
            marginBottom: "40px",
          }}
        >
          {formattedDate}
        </Typography>

        {/* Live Stream Info Box */}
        <Box
          sx={{
            backgroundColor: "rgba(5, 49, 44, 0.95)",
            borderRadius: "0 40px 40px 0", // Rounded only on the right
            padding: "25px 60px",
            display: "flex",
            alignItems: "center",
            gap: "50px",
            marginBottom: "40px",
            minWidth: "980px",
            maxWidth: "fit-content",
            color: "#fff",
          }}
        >
          {/* Left Side - Live Stream */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <Box
                sx={{
                  backgroundColor: "red",
                  color: "#ffffff",
                  padding: "6px 18px",
                  borderRadius: "6px",
                  fontSize: "1.2em",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                }}
              >
                LIVE
              </Box>
              <Typography
                sx={{
                  fontSize: "1.8em",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                STREAM
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: "2.5em",
                fontWeight: 700,
                letterSpacing: "0.05em",
              }}
            >
              {formattedTime}
            </Typography>
          </Box>

          {/* Divider */}
          <Box
            sx={{
              width: "2px",
              height: "70px",
              backgroundColor: "rgba(255, 255, 255, 0.4)",
            }}
          />

          {/* Right Side - Location */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "5px",
            }}
          >
            <Typography
              sx={{
                fontSize: "1.5em",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                opacity: 1,
              }}
            >
              BROADCASTING LIVE FROM
            </Typography>
            <Typography
              sx={{
                fontSize: "2.5em",
                fontWeight: 400,
                textAlign: "center",
              }}
            >
              {venueName}
            </Typography>
            <Typography
              sx={{
                fontSize: "2em",
                fontWeight: 400,
                opacity: 0.9,
              }}
            >
              {city}
            </Typography>
          </Box>
        </Box>

        {/* Broadcast By Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            marginTop: "20px",
          }}
        >
          <Box
            sx={{
              width: "120px",
              height: "2px",
              backgroundColor: "#ffffff",
            }}
          />
          <Typography
            sx={{
              fontSize: "1.8em",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            BROADCAST BY
          </Typography>
          <Box
            sx={{
              width: "120px",
              height: "2px",
              backgroundColor: "#ffffff",
            }}
          />
        </Box>

        {/* Logo */}
        <Box
          sx={{
            marginTop: "20px",
            padding: "20px 40px",
            borderRadius: "15px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={liveStreamingLogo || "/placeholder.svg"}
            alt="The Live Streaming Company"
            style={{
              height: "80px",
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>
    );
  }
);

ConferenceTemplate.displayName = "ConferenceTemplate";
export default ConferenceTemplate;
